import { Box, Button, Chip, CircularProgress, Dialog, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect } from 'react'
import { useState } from 'react'
import api from '../../../service/api'
import { Icons, FLAT_COLORS_WITH_BACKGROUND, formatDate, time24to12 } from '../../../utils/utilities'
import TableHeader from '../../common/TableHeader'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import { QuizBlub } from '../../../assets/custom-icons'
import { toast } from 'react-toastify'
import CustomTableColumnLabel from '../../common/CustomTableColumnLabel'
import ConfirmDialog from '../../common/ConfirmDialog'
import CustomTable from '../../common/custom-table/CustomTable'


const AuditQuizViewer = ( { handleClose, openState, quiz } ) => {

    const [scores, setScores] = useState( [] )
    const [mode, setMode] = useState( "instances" )
    const [deleting, setDeleting] = useState( false )
    const [instances, setInstances] = useState( [] )
    const [loadingScores, setLoadingScores] = useState( false )
    const [fetchingInstances, setFetchingInstances] = useState( false )
    const [sortState, setSortState] = useState( { name: 0, scores: 0 } )
    const [instancesFetchError, setInstancesFetchError] = useState( false )
    const [selectedQuizInstance, setSelectedQuizInstance] = useState( null )
    const [selectedInstanceDetails, setSelectedInstanceDetails] = useState( null )
    const [deleteQuizConfirmDialogState, setDeleteQuizConfirmDialogState] = useState( false )
    const [deleteQuizInstanceConfirmDialogState, setDeleteQuizInstanceConfirmDialogState] = useState( false )


    const { palette, shadows, border, table, layout } = useTheme()

    const scoresColumns = [
        { name: "Name", field: "student_name", renderCell: row => row?.student_details?.name, sortable: true, filterable: true, align: "left", padding: "10px 20px" },
        { name: 'Student ID', field: "student_id", sortable: true, filterable: true, align: "left" },
        { name: 'Total Marks', field: "max_score", align: "center" },
        { name: "Scores Marks", field: "total_score", sortable: true, filterable: true, align: "center", padding: "10px 20px" },
    ]

    const backToInstances = () => {
        setMode( "instances" )
        setSelectedQuizInstance( null )
        setSelectedInstanceDetails( null )
    }

    const deleteQuiz = async () => {
        setDeleting( true )
        try {
            await api.quiz.deleteQuiz( quiz.id )
            handleClose( true )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while deleting the quiz!" )
        } finally {
            setDeleting( false )
        }
    }

    const deleteInstance = async () => {
        setDeleting( true )
        try {
            await api.quiz.deleteInstance( quiz.id, selectedInstanceDetails.id )
            setDeleteQuizInstanceConfirmDialogState( false )
            await fetchQuizInstances()
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong while deleting the quiz instance" )
        } finally {
            setDeleting( false )
        }
    }

    const viewScores = useCallback( async ( instance ) => {
        setSelectedQuizInstance( instance.id )
        setMode( 'scores' )
        setLoadingScores( instance.id )
        setSelectedInstanceDetails( instance )
        try {
            const { data: { scores } } = await api.quiz.fetchQuizInstanceScores( quiz.id, instance.id )
            setScores( scores )
            setSelectedInstanceDetails( instance )
        } catch ( err ) {
            setSelectedQuizInstance( null )
            toast( err?.response?.data?.message || "Something went wrong while fetching scores!" )
        } finally {
            setLoadingScores( null )
        }
    }, [quiz] )

    const fetchQuizInstances = useCallback( async () => {
        setFetchingInstances( true )
        try {
            setInstancesFetchError( true )
            const { data: { data } } = await api.quiz.fetchQuizInstances( quiz.id )
            setInstances( data )
            setFetchingInstances( false )
            if ( data?.length === 1 ) {
                await viewScores( data[0] )
            } else {
                setMode( "instances" )
            }
        } catch ( err ) {
            console.log( err )
            setInstancesFetchError( err?.response?.data?.message || "Opps! An error occured while trying to fetchquiz instances try again." )
        } finally {
            setFetchingInstances( false )
        }
    }, [quiz, viewScores] )

    useEffect( () => {
        fetchQuizInstances()
    }, [fetchQuizInstances] )

    return (
        <Dialog open={openState} slotProps={{
            paper: { sx: { width: "90%", maxWidth: "1200px", display: "flex", flexDirection: "column" } }
        }}>
            {/* Quiz Delete Confirmation Dialog */}
            <ConfirmDialog status={deleteQuizConfirmDialogState} enableSecondaryConfirmation secondaryConfirmationText={quiz.title} actionName='Yes, Delete quiz' message={`All the associated quiz instances will be deleted!`} waiting={deleting} confirmAction={deleteQuiz} cancelAction={() => setDeleteQuizConfirmDialogState( false )} />

            {/* Quiz Instance Delete Confirmation Dialog */}
            <ConfirmDialog status={deleteQuizInstanceConfirmDialogState} enableSecondaryConfirmation secondaryConfirmationText="ConfirmDelete" message={`All the participation details associated with this instances will be deleted!`} waiting={deleting} confirmAction={deleteInstance} cancelAction={() => setDeleteQuizInstanceConfirmDialogState( false )} />


            <Box sx={{ zIndex: 10 }} borderBottom={border[1]} position="sticky" top="0" bgcolor={palette.background.paper} display="flex" alignItems="center" flexWrap="wrap" justifyContent="space-between" padding="20px" >
                <Box>
                    <Typography variant="h1" fontWeight="800" component="h2" fontSize="16px">{quiz.title}</Typography>
                    <Typography variant="subtitle2" fontSize="12px" display="flex" alignItems="center" gap="5px"  > {Icons.default.User} {quiz.creator_data.name}</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary' display="flex" alignItems="center" gap="5px"  >{Icons.default.Dashboard} {quiz.instances} {quiz.instances > 1 ? "Instances" : "Instance"}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap="10px">
                    <Button variant='contained' onClick={() => setDeleteQuizConfirmDialogState( true )} disableElevation color="errorMessage" startIcon={Icons.default.DeleteIconOutlined} sx={{ textTransform: "capitalize" }}>Delete Quiz</Button>
                    <IconButton onClick={() => handleClose()}>{Icons.default.CloseIcon}</IconButton>
                </Box>
            </Box>

            <Box padding="20px" display="flex" minWidth={0} width="100%" flexDirection="column" gap={layout.sectionsGap}>
                <Box>
                    <Typography gutterBottom display="block" variant='subtitle2' color="textSecondary">Topics and number of question</Typography>
                    <Box display="flex" flexWrap="wrap" gap="5px">
                        {quiz.question_bank_topics && quiz.question_bank_topics.map( ( topic, index ) => (
                            <Chip key={topic.id} label={`${topic.name} (${topic.question_count})`} sx={{ marginRight: "5px", borderRadius: "5px", border: border[1], bgcolor: palette.contentBg, fontWeight: "600" }} />
                        ) )}
                    </Box>
                </Box>

                {!fetchingInstances && ( mode === "instances" || !selectedQuizInstance ) && <Box minWidth={0} width="100%">
                    {instances.length > 1 && <Box minWidth={0} display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" marginTop="10px" gap="10px">
                        {instances.map( ( instance, index ) => {
                            let title = ''
                            if ( instance?.access_group?.section_assignment_id ) {
                                title = `${instance.access_group.course_branch_short_name ? instance.access_group.course_branch_short_name + "," : ""} ${instance.access_group?.current_year ? `year: ${instance.access_group?.current_year},` : ""} ${instance.access_group.current_sem ? `Sem: ${instance.access_group.current_sem},` : ""} Sec: ${instance.access_group.section_short_name}`
                            } else
                                title = `${instance.access_group.course_name_short ? instance.access_group.course_name_short + "," : ""} ${instance.access_group?.current_year ? `year: ${instance.access_group?.current_year},` : ""} ${instance.access_group.current_sem ? `Sem: ${instance.access_group.current_sem},` : ""} Batch: ${instance.access_group.batch_short_name}`

                            return <Box borderRadius="5px" flexGrow={1} justifyContent="space-between" alignItems="center" boxShadow={shadows[1]} border={border[1]} sx={{ cursor: "pointer", transition: "200ms", "&:hover": { boxShadow: shadows[2] } }} bgcolor={palette.background.paper} key={instance.id}>
                                <Box display="flex" padding="20px" gap="20px" alignItems="flex-start">
                                    <img src={QuizBlub} alt="Quiz Instance" />
                                    <Box flexGrow={1} overflow="hidden">
                                        <Tooltip title={title}>
                                            <Typography noWrap fontSize="14px" variant="subtitle1"><strong><font color={palette.primary.main}>Quiz for</font> <em>{title}</em> </strong> </Typography>
                                        </Tooltip>
                                        <Typography fontSize="12px" variant="subtitle2" color="GrayText">
                                            {formatDate( instance.start_date )}
                                        </Typography>
                                        <Typography fontSize="12px" variant="subtitle2" color="GrayText">
                                            {time24to12( instance.start_time )} - {time24to12( instance.end_time )}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box padding="5px 20px" borderTop={`1px solid ${palette.borderColor}`} display="flex" gap="10px" alignItems="center">
                                    <Button onClick={() => viewScores( instance )} disabled={loadingScores === instance.id} startIcon={loadingScores === instance.id ? <CircularProgress size={14} /> : Icons.default.AnalyticsIconOutlined} color="customThemeColor" sx={{ textTransform: "capitalize" }} >View Scores</Button>
                                </Box>
                            </Box>
                        } )}
                    </Box>}
                    {instancesFetchError && <Typography color="errorMessage.main" variant='subtitle2'>{instancesFetchError}</Typography>}
                </Box>}

                {mode === "instances" && instances.length === 0 && <Typography textAlign="center" paddingY="40px" variant='subtitle2'  >
                    No Instanceses were created for this quiz!
                </Typography>}

                {!fetchingInstances && mode === "scores" && selectedQuizInstance && <Box border={border[1]} borderRadius={layout.borderRadius} marginTop={layout.sectionsGap} overflow="hidden" bgcolor={palette.background.paper}>
                    <Box borderBottom={border[1]} display="flex" flexWrap="wrap" padding="10px 20px" bgcolor={palette.contentBg} color={table.headerColor} alignItems="center" gap={layout.sectionsGap}>
                        {instances.length > 1 && <IconButton size="small" onClick={backToInstances}>{Icons.default.ArrowBack}</IconButton>}
                        <Box flexGrow={1}>
                            <Typography variant='subtitle1' color='textSecondary' ><small>{formatDate( selectedInstanceDetails?.start_date, { format: "ll" } )}</small> </Typography>
                            {selectedInstanceDetails?.access_group?.batch_assignment_id && <Typography marginTop="-5px" variant='subtitle1' >  SCORES - <em>{selectedInstanceDetails.access_group.branch_name}, year: {selectedInstanceDetails.access_group.current_year}, sem: {selectedInstanceDetails.access_group.current_sem}, {selectedInstanceDetails.access_group.batch_short_name}</em></Typography>}
                            {selectedInstanceDetails?.access_group?.section_assignment_id && <Typography marginTop="-5px" variant='subtitle1' >  SCORES -  <em>{selectedInstanceDetails.access_group.course_branch_short_name}, year: {selectedInstanceDetails.access_group.current_year}, Section: {selectedInstanceDetails.access_group.section_short_name}</em></Typography>}
                        </Box>
                        <Typography variant='subtitle2' > <strong>{scores?.length || 0} participants</strong> </Typography>
                        <Button variant='contained' onClick={() => setDeleteQuizInstanceConfirmDialogState( true )} disableElevation color="errorMessage" startIcon={Icons.default.DeleteIconOutlined} sx={{ textTransform: "capitalize" }}>Delete</Button>
                    </Box>
                    <Box>
                        {loadingScores && <DataLoadingSpinner waitingMessage="Loading scores..." />}

                        {!loadingScores && scores.length > 0 && <CustomTable tableKey="quizviewer" containerStyles={{ border: "none", maxHeight: "350px" }} columns={scoresColumns} data={scores} dataIdKey="id" />}

                        {!loadingScores && scores.length === 0 && <Typography padding="20px" textAlign="center" display="block" variant='subtitle2'  >
                            No participations for this quiz!
                        </Typography>}
                    </Box>
                </Box>}

                {fetchingInstances && <DataLoadingSpinner waitingMessage="Loading the quiz instances please wait..." />}
            </Box>
        </Dialog>
    )
}

export default AuditQuizViewer
